<i18n src="./translations"></i18n>

<template>
  <div
    :class="classes"
    :data-preload="$options.name"
    class="price-info-21__prices qa-priceInfo21__prices"
  >
    <div class="top-section">
      <!-- [New Pricing Version] The new Price version {PriceTag} is currently enabled for B2C only, but in the future should be used for both -->
      <PriceTag
        v-if="uvpLegalSafeActive"
        :unit-prices="product.unitPrices"
        :price-texts="product.priceTexts"
        :prices="product.prices"
        :is-kvi="isKvi"
        :discount-label="priceDiscountLabel"
      />

      <!-- [Old Pricing Version] The content of this Template is currently only used for BPlus -->
      <div v-if="!uvpLegalSafeActive">
        <div
          v-if="product.unitPrices && product.unitPrices[0]"
          class="price-info-21__price-wrap"
        >
          <Price
            v-if="!showUpToDiscountedPrice"
            :label="product.unitPrices[0].label"
            :is-kvi="isKvi"
            :value="getPrice"
            :is-net="isBusinessCustomer"
            class="price-info-21__main-price qa-PriceInfo21__mainPrice"
            :modifiers="priceModifiers"
          />

          <PriceDiscounted
            v-else
            is21
            :price-texts="product.priceTexts"
            :unit-prices="product.unitPrices"
            :prices="product.prices"
            :price-modifiers="priceModifiers"
            :is-kvi="isKvi"
          />
        </div>

        <span
          v-if="showRecommendedPrice"
          class="price-info-21__retail-price-container qa-PriceInfo__retailPrice"
        >
          {{ $t('price-info-21.price_text_uvp') }}
          <span
            class="price-info-21__retail-price"
            :class="{
              'is-striked': !isBusinessCustomer,
            }"
            >{{ product.priceTexts.recommendedRetailPrice }}</span
          >
        </span>

        <price-savings :product="product" is21 />

        <div class="price-info-21__price-tax">
          <span
            v-if="product.priceTexts && pricePerUnit && pricePerUnit.value"
            class="price-info-21__unit-price qa-PriceInfo21__unitPrice"
          >
            {{ pricePerUnit.value }}/{{ pricePerUnit.unit }},
          </span>

          <span v-if="showTaxes" class="qa-PriceInfo21__tax">
            {{
              $t('price-info-21.tax_text', {
                '%taxPercentage%': product.priceTexts.tax,
              })
            }},
          </span>

          <MaskedLink
            v-if="!isDirectDelivery"
            class="price-info-21__shipping"
            :text="`${$t('price-info-21.excluding-shipping')}${
              hasDepositPrice ? ',' : ''
            }`"
            :redirect-to="frontFacingPath('content.page.shipping')"
            :with-underline="true"
            :aria-label="$t('price-info-21.shipping-link-label')"
            role="link"
            tabindex="0"
            target="_blank"
          />

          <span
            v-if="hasDepositPrice"
            aria-hidden="true"
            class="qa-PriceInfo21__deposit"
            >{{
              $t('price-info-21.deposit_price', {
                '%formattedDepositPrice%': product.priceTexts.depositPrice,
              })
            }}</span
          >
        </div>
      </div>

      <!-- PriceInfo Actions (used for Wishlist Button) for B2B & B2C versions -->
      <div v-if="$slots.default" class="actions-slot">
        <slot />
      </div>
    </div>

    <div v-if="availabilityEnabled" class="price-info-21__availability-wrapper">
      <PdpAvailability v-if="!isOTCSalesChannel" :state="productAvailability" />
      <BranchesAvailabilityLink :product="product" label-type="secondary" />
    </div>

    <PriceTexts
      v-if="priceTextsEnabled"
      :modifiers="modifiers.includes('slim') ? ['slim'] : []"
      :price-texts="product.priceTexts"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import globalMixin from 'Libs/mixins/globalMixin';
import has from 'lodash/has';
import Price from 'Components/01-atoms/price/Price';
import PriceTag from 'Components/02-molecules/price-tag/PriceTag';
import PriceTexts from 'Components/01-atoms/price-texts/PriceTexts';
import PdpAvailability from 'Components/01-atoms/pdp-availability/PdpAvailability';
import BranchesAvailabilityLink from 'Components/01-atoms/branches-availability-link/BranchesAvailabilityLink';
import MaskedLink from 'Components/01-atoms/masked-link/MaskedLink';
import PriceDiscounted from 'Components/01-atoms/price-discounted/PriceDiscounted';
import PriceSavings from 'Components/01-atoms/price-savings/PriceSavings';

export default {
  name: 'PriceInfo21',
  components: {
    Price,
    PriceTag,
    PdpAvailability,
    BranchesAvailabilityLink,
    PriceTexts,
    MaskedLink,
    PriceDiscounted,
    PriceSavings,
  },
  mixins: [globalMixin],
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    quantity: {
      type: Number,
      default: 1,
    },
    priceTextsEnabled: {
      type: Boolean,
      default: true,
    },
    availabilityEnabled: {
      type: Boolean,
      default: true,
    },
    isPlp: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isWishedProduct: false,
    };
  },
  computed: {
    ...mapState('renderingSalesChannelConfig', {
      isU2dAktionEnabled: ({ isU2dAktionEnabled }) => isU2dAktionEnabled,
    }),
    ...mapState('core', {
      uvpLegalSafeActive: ({ salesChannel }) =>
        !!salesChannel?.config?.uvpLegalSafeActive,
    }),
    ...mapState('renderingSalesChannelConfig', ['isKviLabelEnabled']),
    ...mapGetters('core', [
      'isBusinessCustomer',
      'getCompanyInformation',
      'isOTCSalesChannel',
    ]),
    hasDepositPrice() {
      return this.product.priceTexts && this.product.priceTexts.depositPrice;
    },
    isDirectDelivery() {
      return (
        (this.isBusinessCustomer &&
          this.getCompanyInformation?.isDirectDelivery) ||
        false
      );
    },
    showTaxes() {
      return (
        this.product.priceTexts &&
        this.product.priceTexts.tax &&
        !this.isBusinessCustomer
      );
    },
    showUpToDiscountedPrice() {
      const priceTexts = this.product?.priceTexts;
      const discountedSalesValueGross = priceTexts?.discountedSalesValueGross;
      const upToDiscountPercentage = priceTexts?.upToDiscountPercentage;

      return (
        discountedSalesValueGross &&
        discountedSalesValueGross !== 0 &&
        upToDiscountPercentage &&
        upToDiscountPercentage > 0 &&
        this.isU2dAktionEnabled
      );
    },
    pricePerUnit() {
      const ppu = !this.isBusinessCustomer
        ? this.product.priceTexts.pricePerUnit
        : this.product.priceTexts.netPricePerUnit;

      const dppu = !this.isBusinessCustomer
        ? this.product.priceTexts.discountedPricePerUnit
        : this.product.priceTexts.discountedNetPricePerUnit;

      return !this.showUpToDiscountedPrice ? ppu : dppu;
    },
    getPrice() {
      // B2B => net price is displayed
      if (this.isBusinessCustomer) {
        const discountedSalesValuesNet = this.product?.priceTexts
          ?.discountedSalesValuesNet;

        if (discountedSalesValuesNet && discountedSalesValuesNet !== 0) {
          return discountedSalesValuesNet;
        }

        if (this.product.unitPrices && this.product.unitPrices.length > 1) {
          return this.product?.unitPrices[1]?.price;
        }
      }

      // B2C
      const discountedSalesValueGross = this.product?.priceTexts
        ?.discountedSalesValueGross;

      return discountedSalesValueGross && discountedSalesValueGross !== 0
        ? discountedSalesValueGross
        : this.product?.unitPrices[0]?.price;
    },
    productAvailability() {
      return this.product.availability;
    },
    priceModifiers() {
      const modifiers = ['big'];
      const product = this.product;

      if (
        has(product, ['salesOffer', 'formerPrice']) ||
        (has(product, ['priceTexts', 'recommendedRetailPrice']) &&
          product.priceTexts.recommendedRetailPrice.length > 0)
      ) {
        modifiers.push('highlighted');
      }

      if (this.theme === 'kfzteile-com') {
        modifiers.push('com');
      }
      return modifiers;
    },
    showRecommendedPrice() {
      const product = this.product;
      const hasRecommendedPrice =
        has(product, ['priceTexts', 'recommendedRetailPrice']) &&
        product.priceTexts.recommendedRetailPrice.length > 0;
      const hasPrices =
        has(product, ['prices', 'price']) && has(product, ['prices', 'rrp']);

      // if (this.isBusinessCustomer) {
      //   return false;
      // }

      if (!hasRecommendedPrice && !hasPrices) {
        return false;
      }

      if (hasPrices && product.prices.price > product.prices.rrp) {
        return false;
      }

      return true;
    },
    ...mapState('core', {
      theme: ({ salesChannel }) => salesChannel.theme,
      // TODO [izerozlu] remove 'salesChannelId' access after the disabling is not needed
      salesChannelId: ({ salesChannel }) => salesChannel.salesChannelId,
    }),
    ...mapState('wishlist', {
      isWished: (state) => state.data.isWished || false,
    }),
    isNewAvailability() {
      if (this.product && this.product.availability) {
        const { availability } = this.product;
        return (
          typeof availability === 'string' || availability instanceof String
        );
      }
      return false;
    },
    availabilityKey() {
      if (this.isNewAvailability) {
        return this.product.availability;
      }
      return this.product.availability ? this.product.availability.key : '';
    },
    isKvi() {
      return this.product?.isKvi && this.isKviLabelEnabled;
    },
    priceDiscountLabel() {
      return {
        type: this.product?.discountLabelType || '',
        text: this.product?.discountLabelText || '',
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import 'variables/breakpoints';
@import 'mixins';

.priceInfo21 {
  display: var(--price-info-21-root-display, flex);
  flex-direction: column;
  gap: var(--space-2);

  .top-section {
    display: var(--price-info-21-top-section-display, flex);

    > *:not(.actions-slot) {
      grid-area: var(--price-info-21-price-area-grid-area);
    }

    .actions-slot {
      margin-left: auto;
      grid-area: var(--price-info-21-actions-slot-grid-area);
      justify-self: var(--price-info-21-actions-slot-justify-self);
      align-self: var(--price-info-21-actions-slot-align-self);
    }
  }

  .price-info-21__former-price {
    font-size: var(--font-size-ML);
  }

  .price-info-21__shipping {
    color: var(--color-primary);
  }

  @media #{$_mediaSDown} {
    &--slim {
      .priceTax {
        font-size: 11px;
        line-height: 1.3em;
      }
    }

    .price-info-21__price-tax .qa-PriceInfo21__tax {
      font-size: var(--font-size-S) !important;
    }
  }
}

.PriceInfo21--slide .price-info-21__prices {
  display: flex;
  flex-flow: column;
}

.price-info-21__price-wrap {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;

  @media #{$_mediaSDown} {
    margin-bottom: var(--space-0--half);
  }
}

.price-info-21__wishlist-button {
  display: flex;
  flex-grow: 99;
  justify-content: flex-end;
}

.PriceInfo21--slide .price-info-21__price-wrap {
  flex-wrap: nowrap;
}

.price-info-21__main-price {
  font-size: var(--font-size-LXL);
  color: var(--color-nero);
  margin: var(--space-0--half) var(--space-1--half) 0 0;

  &.price--highlighted,
  &.price--outdated,
  &.price--big {
    color: var(--color-nero);
    font-size: var(--font-size-LXL);
  }

  .PriceInfo21--slide & {
    font-size: var(--font-size-L);
    line-height: 29px;
  }
}

.price-info-21__former-price {
  font-size: var(--font-size-ML);
}

.price-info-21__price-tax {
  font-size: var(--font-size-SM);
  line-height: 1.29em;
  color: var(--color-hippo-darker);
  margin-top: var(--space-0--half);

  .PriceInfo21--slide & {
    font-size: var(--font-size-S);
    line-height: 14px;
    min-height: 28px;
  }
}

.price-info-21__retail-price-container {
  font-size: var(--font-size-M);
  color: var(--color-nero);
  line-height: var(--font-size-MLL);
  margin-top: var(--space-0--half);

  .price-savings {
    margin-left: var(--space-1);
  }
}

.price-info-21__retail-price {
  white-space: nowrap;

  &.is-striked {
    text-decoration: line-through;
  }
}

.price-info-21__availability-wrapper {
  grid-area: var(--price-info-21-availability-grid-area);
}

.price-info-21__availabilit-wrapper {
  margin-bottom: 10px;

  .PriceInfo21--slide & {
    order: -1;
    margin: var(--space-1) 0;
  }
}

.price-info-21__tax-comma {
  margin-left: -3px;
  margin-right: -3px;
}

@media #{$_mediaMDown} {
  .price-info-21__price-wrap {
    flex-flow: row wrap;
    margin-bottom: 6px;

    .price-info-21__main-price {
      width: 100%;
      margin-bottom: 8px;
      font-size: var(--font-size-L);
    }

    .price-info-21__retail-price-container {
      font-size: var(--font-size-SM);
      margin-top: 5px;
    }
  }
}

.priceTexts {
  grid-area: var(--price-info-21-price-texts-grid-area);
  padding-bottom: var(--price-info-21-price-texts-padding-bottom, 0);

  @media #{$_mediaMDown} {
    font-size: var(--font-size-SM);
    padding-top: var(--space-2);
  }
}

@media #{$_mediaMDown} {
  .price-info-21__main-price {
    font-size: var(--price-info-21-main-price-font-size, var(--font-size-L));
  }
}

@media screen and (max-width: 919px) {
  .price-info-21__wishlist-button {
    &--hidden {
      display: none;
    }
  }
}

::v-deep {
  .branchesAvailabilityLink {
    padding: 0;
    margin-top: var(--space-XXS);

    img {
      display: none;
    }

    .link {
      margin: 0;
    }
  }
}
</style>
